import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
  ArrowLeft, ArrowRight, BookOpen, GraduationCap, Users, HelpCircle,
  ChevronDown, FlaskConical, ShieldCheck, FileDown, Sparkles, ScanLine,
  Link2, Check,
} from "lucide-react";

const BRUT = "border-2 border-[#0F172A] shadow-[4px_4px_0px_#0F172A]";
const BRUT_HOVER =
  "transition-all duration-200 ease-in-out hover:-translate-y-[3px] " +
  "hover:shadow-[6px_6px_0px_#0F172A]";

// Top-level sections of the instructions, used to build both the TOC and the
// content. Adding/removing an entry here automatically updates both.
const SECTIONS = [
  { id: "dla-nauczyciela", label: "Dla Nauczyciela", icon: GraduationCap, color: "bg-[#FEF08A]" },
  { id: "dla-ucznia", label: "Dla Ucznia", icon: Users, color: "bg-[#E0F2FE]" },
  { id: "ocenianie", label: "Jak działa ocenianie", icon: FlaskConical, color: "bg-[#D1FAE5]" },
  { id: "bezpieczenstwo", label: "Bezpieczeństwo konta", icon: ShieldCheck, color: "bg-[#FFEDD5]" },
  { id: "faq", label: "FAQ", icon: HelpCircle, color: "bg-[#FEF08A]" },
];

export default function Instrukcja() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const containerRef = useRef(null);

  // Highlight the TOC item whose section is closest to the top of the viewport.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: 0 },
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#F8FAFC]/85 backdrop-blur-md
                         border-b-2 border-[#0F172A]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold"
                data-testid="instrukcja-back-home">
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} />
            <span>Wróć</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/teacher/login"
                  className={`${BRUT} ${BRUT_HOVER} bg-white px-4 py-2 rounded-md
                              font-bold text-sm hover:bg-[#FEF08A]`}>
              Panel Nauczyciela
            </Link>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b-2 border-[#0F172A] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <span className="text-[11px] tracking-[0.25em] uppercase font-black
                           bg-[#FEF08A] px-3 py-1 border-2 border-[#0F172A]">
            Dokumentacja
          </span>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl
                         tracking-tight leading-[0.95] mt-5">
            Instrukcja <span className="bg-[#D1FAE5] px-2 border-2 border-[#0F172A]
                                        shadow-[4px_4px_0px_#0F172A] inline-block">obsługi</span>
          </h1>
          <p className="text-base sm:text-lg text-[#334155] mt-5 max-w-2xl">
            Wszystko czego potrzebujesz aby uruchomić pierwszą kartkówkę dzisiaj.
            Sekcje są krótkie i zawierają konkretne przykłady.
          </p>
        </div>
      </section>

      {/* Body: TOC + content */}
      <div ref={containerRef} className="max-w-6xl mx-auto px-6 py-12 grid lg:grid-cols-12 gap-10">
        {/* Sidebar TOC */}
        <aside className="lg:col-span-3" data-testid="instrukcja-toc">
          <nav className={`${BRUT} bg-white rounded-md p-4 lg:sticky lg:top-24`}>
            <div className="text-[10px] tracking-[0.25em] uppercase font-black
                            text-[#64748B] mb-3 flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5" strokeWidth={2.5} />Spis treści
            </div>
            <ul className="space-y-1.5">
              {SECTIONS.map((s) => {
                const active = activeId === s.id;
                return (
                  <li key={s.id}>
                    <a href={`#${s.id}`}
                       className={`flex items-center gap-2.5 px-2.5 py-2 rounded-md
                                   text-sm font-bold transition-colors
                                   ${active
                                     ? "bg-[#0F172A] text-white"
                                     : "hover:bg-[#F1F5F9] text-[#0F172A]"}`}
                       data-testid={`toc-link-${s.id}`}>
                      <s.icon className="w-4 h-4 shrink-0" strokeWidth={2.5} />
                      <span>{s.label}</span>
                      {active && <ArrowRight className="w-3.5 h-3.5 ml-auto" strokeWidth={3} />}
                    </a>
                  </li>
                );
              })}
            </ul>
            <div className={`${BRUT} bg-[#FEF08A] rounded-md p-3 mt-4`}>
              <div className="text-[10px] uppercase tracking-[0.2em] font-black">
                Krótka droga
              </div>
              <p className="text-xs mt-1 leading-snug">
                Pierwsza kartkówka w 5 minut: <b>Rejestracja → Wybór 10 pytań → Kod sesji</b>.
              </p>
            </div>
          </nav>
        </aside>

        {/* Content */}
        <article className="lg:col-span-9 space-y-12">
          <Section id="dla-nauczyciela" title="Dla Nauczyciela"
                   icon={GraduationCap} chip="bg-[#FEF08A]">
            <StepRow n="1" title="Rejestracja i logowanie"
                     body={<>Załóż konto klikając „Jestem nauczycielem&rdquo; → „Zarejestruj konto&rdquo;.
                            Aktywuj konto klikając w link wysłany na e-mail. Pierwsze logowanie
                            wymaga weryfikacji e-mail. Hasło bcrypt-hashowane, nie przechowujemy
                            jawnego.</>} />
            <StepRow n="2" title="Utwórz klasę" icon={Users}
                     body={<>Przejdź do <b>„Klasy&rdquo;</b> w nagłówku i kliknij
                            <b> „+ Nowa klasa&rdquo;</b>. Nadaj jej nazwę (np. „3A chemia
                            rozszerzona&rdquo;) i opcjonalnie krótki opis. Klasa działa jako
                            <b> tag</b> pod który później podepniesz kartkówki i wyniki —
                            możesz filtrować sesje i statystyki per klasa. Klas możesz mieć
                            dowolnie wiele, są prywatne (widzisz tylko Ty).</>} />
            <StepRow n="3" title="Tworzenie pytań" icon={Sparkles}
                     body={<>Przejdź do <b>„Pytania&rdquo;</b> w nagłówku. Dwa typy własności:
                            <ul className="list-disc pl-5 mt-2 space-y-1">
                              <li><b>Wspólne</b> — moderowane przez administratora, dostępne dla wszystkich (tylko do odczytu).</li>
                              <li><b>Twoje</b> — tworzysz sam, widzisz tylko Ty.</li>
                            </ul>
                            Każde pytanie należy do <b>zakresu</b> (klasa 1–3 podstawowej lub
                            1–4 rozszerzonej) i ma <b>temat</b>. Możesz dodać obrazek
                            i&nbsp;wyjaśnienie poprawnej odpowiedzi. Jeśli chcesz dodać pytanie
                            do <b>puli wspólnej</b> — użyj przycisku <b>„Zaproponuj
                            pytanie&rdquo;</b>; propozycję zatwierdza administrator.
                            {" "}<b>Generator AI</b> (1–20 pytań z wybranego tematu przez
                            Claude Sonnet) jest dostępny <b>tylko dla administratora</b> puli
                            wspólnej — z opcjonalnym drugim modelem-sędzią, który stara się
                            odsiewać pytania z błędami merytorycznymi.</>} />
            <StepRow n="4" title="Tworzenie kartkówki"
                     body={<>Na dashboardzie kliknij <b>„Nowa kartkówka&rdquo;</b>. Wybierz tytuł,
                            czas (minuty), liczbę pytań i opcjonalnie klasę.
                            <b> Trzy tryby doboru pytań</b> (zakładki w oknie):
                            <ul className="list-disc pl-5 mt-2 space-y-1">
                              <li><b>🎲 Automatyczny</b> — system sam losuje pytania z zakresu klasy.
                                Możesz określić <b>rozkład pytań per temat</b>
                                (np. 4 × Mol, 3 × Roztwory, 3 × Reakcje = 10 pytań).
                                Najszybszy sposób — dobry na codzienną kartkówkę.</li>
                              <li><b>🔀 Cross-scope (mieszany zakres)</b> — dla każdego tematu
                                osobno wybierasz <b>z jakiej klasy</b> mają lecieć pytania
                                (np. Mol z klasy 2 rozszerzenie, Roztwory z klasy 1 podstawa).
                                Przydatne przed maturą albo przy powtórkach międzyklasowych.</li>
                              <li><b>✋ Ręczny wybór</b> — otwierasz listę wszystkich pytań
                                z zaznaczonego zakresu, filtrujesz po temacie/tekście
                                i <b>ręcznie zaznaczasz</b> konkretne pytania. Pełna kontrola
                                nad zestawem — dobre na wejściówki, kolokwia, powtórki
                                „po Twojemu&rdquo;.</li>
                            </ul>
                            <span className="block mt-3">
                              <b>Opcje anti-ściąganie</b> (checkboxy w oknie):
                            </span>
                            <ul className="list-disc pl-5 mt-1 space-y-1">
                              <li><b>🎯 Losuj kolejność pytań per uczeń</b> — każdy uczeń dostanie
                                ten sam zestaw pytań, ale w innej kolejności. Sąsiad nie ma jak
                                podpatrzeć „które pytanie 3?&rdquo;. Zapamiętane per uczeń, więc
                                po odświeżeniu widzi tę samą kolejność.</li>
                              <li><b>👥 Grupy A/B (dwa kody, różne pytania)</b> — najsilniejsze
                                zabezpieczenie. Kartkówka dostanie <b>dwa kody</b>, pół klasy
                                wchodzi kodem A, druga połowa kodem B. Oba kody mają pytania
                                z tych samych tematów, ale <b>całkowicie różne treści</b>.
                                Wystartują i skończą się jednocześnie; wyniki lecą do wspólnej
                                statystyki. W trybach <b>Automatyczny</b> i <b>Cross-scope</b>
                                wymaga 2× więcej pytań na temat w puli. W trybie <b>✋ Ręczny</b>
                                zobaczysz dwa taby (Grupa A / Grupa B) — wybierasz pytania
                                osobno do każdej puli, system pilnuje żeby nie powtarzały się.</li>
                            </ul>
                            <span className="block mt-2 brut bg-[#D1FAE5] rounded-md px-3 py-2 text-xs">
                              <b>💡 Możesz łączyć oba anti-cheat:</b> Grupy A/B rozdzielają
                              <i> pule pytań</i>, a losowa kolejność miesza <i>sekwencję wewnątrz
                              każdej grupy</i>. Razem = maksymalna ochrona przed ściąganiem.
                            </span>
                            <span className="block mt-2">
                              Nie musisz kończyć od razu — <b>„Zapisz jako szkic&rdquo;</b> odłoży
                              konfigurację, a wrócisz do niej z panelu <b>„Moje szkice&rdquo;</b>.
                            </span>
                            <span className="block mt-2">
                              Masz gotową kartkówkę i chcesz uruchomić ją w kolejnej klasie?
                              Użyj <b>📋 Duplikuj</b> na karcie sesji — pojawi się mini-picker
                              klasy i nowa sesja dostanie własny kod, gotowa do uruchomienia.
                            </span></>} />
            <StepRow n="5" title="Prowadzenie sesji" icon={ScanLine}
                     body={<>Po utworzeniu zobaczysz <b>6-znakowy kod</b> (lub dwa kody
                            A i B w trybie Grupy A/B — wtedy również <b>dwa osobne kody QR</b>,
                            po jednym dla każdej grupy) oraz linka „Dołącz". Uczniowie skanują QR
                            albo wchodzą na <Link to="/join" className="underline font-bold"> stronę
                            &bdquo;Dołącz&rdquo;</Link> i wpisują kod.
                            Klikasz „Rozpocznij&rdquo; gdy klasa gotowa. Podczas sesji widzisz:
                            <ul className="list-disc pl-5 mt-2 space-y-1">
                              <li><b>⏱ Zegar odliczający</b> do końca kartkówki (żółty ≤2 min,
                                czerwony pulsujący ≤30 s).</li>
                              <li><b>Live status każdego ucznia</b> — awatar z numerem
                                w dzienniku i etykieta: <b>„Dołączył&rdquo;</b> (poczekalnia, przed startem),
                                <b>„Pisze&rdquo;</b> (sesja aktywna) lub <b>„Oddane&rdquo;</b> z wynikiem.</li>
                              <li>W trybie Grupy A/B — widget <b>👥 Grupy A vs B</b> z balansem
                                dołączeń (zielony ✓ gdy zrównoważone, czerwony ⚠ w innym wypadku)
                                oraz porównanie średnich (alert gdy różnica ≥8 pp).</li>
                              <li><b>🧹 Usuń duplikaty</b> — jeśli uczeń przypadkiem dołączył kilka razy,
                                jeden klik czyści nadmiarowe puste wpisy. Prace już oddane są
                                chronione i nie zostaną skasowane.</li>
                            </ul>
                            <span className="block mt-2">
                              <b>Automatyczne zakończenie</b>: po upływie limitu czasu system sam
                              flipuje sesję na „Zakończona&rdquo;, automatycznie oddaje arkusze
                              uczniów którzy nie zdążyli kliknąć „Oddaj&rdquo; (nieodpowiedziane
                              pytania liczą się jako błędne), i wysyła Ci powiadomienie
                              na dashboardzie. Możesz też zakończyć wcześniej klikając
                              <b> „Zakończ&rdquo;</b> — działa tak samo.
                            </span></>} />
            <StepRow n="6" title="Wyniki i przegląd pytań" icon={FileDown}
                     body={<>Po zakończonej sesji <b>pobierz wyniki</b> w PDF/CSV —
                            indywidualnie lub zbiorczo. <b>Statystyki per pytanie</b>
                            pokazują najtrudniejsze pytania w klasie (jaki% klasy odpowiedziało
                            źle). Dodatkowo panel <b>„Użyte pytania&rdquo;</b> wyświetla całą pulę
                            wykorzystaną w kartkówce — dla pytań ze wspólnej puli możesz
                            kliknąć <b>„Zgłoś poprawkę&rdquo;</b>, żeby wysłać administratorowi
                            uwagę (np. „poprawna odpowiedź to B, nie A&rdquo;) prosto z widoku
                            sesji.</>} />
          </Section>

          <Section id="dla-ucznia" title="Dla Ucznia"
                   icon={Users} chip="bg-[#E0F2FE]">
            <StepRow n="A" title="Dołączanie"
                     body={<>Wejdź na <Link to="/join" className="underline font-bold">
                            stronę dołączania</Link> lub kliknij „Jestem uczniem&rdquo; na stronie
                            głównej. Wpisz <b>6-znakowy kod</b> od nauczyciela oraz
                            <b> imię (bez nazwiska)</b> i <b>numer w dzienniku</b> (1–40).
                            <b> Nie potrzebujesz konta ani maila</b> — pełna zgodność z RODO.
                            Jeśli klikniesz „Dołącz&rdquo; drugi raz z tego samego urządzenia,
                            wrócisz do swojej rozpoczętej pracy zamiast tworzyć nowy wpis.</>} />
            <StepRow n="B" title="Rozwiązywanie kartkówki"
                     body={<>Po starcie sesji odpowiadaj na kolejne pytania. Twoja odpowiedź
                            <b> zapisuje się automatycznie</b> co 1.2&nbsp;sekundy — możesz
                            odświeżyć stronę, zmienić urządzenie czy stracić internet, postęp
                            pozostaje. W każdym momencie widzisz pozostały czas.
                            {" "}<b>Jeśli nie zdążysz kliknąć „Oddaj&rdquo;</b> przed końcem czasu
                            — system i tak wyśle Twoje zapisane odpowiedzi automatycznie
                            (nieodpowiedziane pytania liczą się jako błędne). Nauczyciel
                            może też zakończyć wcześniej — zostaniesz od razu przekierowany
                            do widoku wyników z komunikatem, że praca została zapisana.</>} />
            <StepRow n="C" title="Wyniki"
                     body={<>Po oddaniu zobaczysz <b>wynik procentowy</b> oraz <b>wyjaśnienia
                            błędnych odpowiedzi</b> (jeśli nauczyciel je dodał). Możesz pobrać
                            swój arkusz w&nbsp;PDF.</>} />
          </Section>

          <Section id="ocenianie" title="Jak działa ocenianie"
                   icon={FlaskConical} chip="bg-[#D1FAE5]">
            <p className="leading-relaxed">
              Aplikacja używa <b>algorytmu Damerau-Levenshteina</b> z chemiczno-językowymi
              tolerancjami. Konkretnie akceptujemy:
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {[
                { l: "Polskie znaki opcjonalne",
                  r: '„wegla" = „węgla" = „WĘGLA"' },
                { l: "Wzory chemiczne — różne zapisy",
                  r: '„H2O" = „H₂O" = „H_2O"' },
                { l: "Drobne literówki",
                  r: '„siakra" → „siarka" (1 zamiana znaku)' },
                { l: "Jednostki opcjonalne",
                  r: '„48" = „48 g" = „48g"' },
                { l: "Synonimy w nawiasach",
                  r: '„hydrat (kryształowodan)" — oba akceptowane' },
                { l: "Wielkość liter",
                  r: '„HCl" = „hcl" = „HCL" (zawsze)' },
              ].map((row, i) => (
                <div key={i} className={`${BRUT} bg-white rounded-md p-4`}>
                  <div className="font-bold text-sm">{row.l}</div>
                  <div className="text-sm font-mono mt-1 text-[#475569]">{row.r}</div>
                </div>
              ))}
            </div>
            <Callout color="bg-[#FEF08A]">
              <b>Tolerancja jest świadoma.</b> Dla pytań ABCD algorytm wymaga dokładnego
              wyboru — żadnych domysłów. Dla pytań otwartych zostawiamy ~10% marginesu
              błędu na literówki, ale nie tolerujemy fundamentalnych pomyłek pojęciowych.
            </Callout>
          </Section>

          <Section id="bezpieczenstwo" title="Bezpieczeństwo konta"
                   icon={ShieldCheck} chip="bg-[#FFEDD5]">
            <p className="leading-relaxed">
              W Ustawieniach (<Link to="/teacher/settings"
              className="underline font-bold">/teacher/settings</Link>) możesz włączyć:
            </p>
            <ul className="space-y-2 mt-3">
              <li className="flex gap-3 items-start">
                <span className={`${BRUT} bg-[#FEF08A] text-[10px] uppercase
                                   tracking-[0.15em] font-black px-2 py-0.5 mt-0.5`}>
                  2FA
                </span>
                <span><b>Logowanie 2-etapowe</b> przez kod email — po wpisaniu hasła
                  wymagany jest 6-cyfrowy kod wysłany na adres e-mail.</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className={`${BRUT} bg-[#D1FAE5] text-[10px] uppercase
                                   tracking-[0.15em] font-black px-2 py-0.5 mt-0.5`}>
                  Zaufane
                </span>
                <span><b>Zaufane urządzenia</b> — pomiń 2FA na 60 dni dla tego komputera/
                  telefonu. Możesz cofnąć zaufanie pojedynczego urządzenia lub wszystkich
                  na raz.</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className={`${BRUT} bg-[#E0F2FE] text-[10px] uppercase
                                   tracking-[0.15em] font-black px-2 py-0.5 mt-0.5`}>
                  Alert
                </span>
                <span><b>Alert nowego logowania</b> — gdy ktoś loguje się z nowego
                  kraju lub przeglądarki, dostajesz e-mail z lokalizacją i instrukcją
                  „Wyloguj wszędzie&rdquo;.</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className={`${BRUT} bg-[#FFEDD5] text-[10px] uppercase
                                   tracking-[0.15em] font-black px-2 py-0.5 mt-0.5`}>
                  Sesje
                </span>
                <span><b>Wyloguj wszystkie urządzenia</b> — jeden klik unieważnia wszystkie
                  tokeny i cofa wszystkie zaufane urządzenia. Przydatne gdy podejrzewasz
                  przejęcie.</span>
              </li>
            </ul>
            <Callout color="bg-[#FFEDD5]">
              <b>Po zmianie hasła</b> lub <b>resetowaniu hasła</b> wszystkie zaufane
              urządzenia są automatycznie cofane — wymagają ponownego 2FA.
            </Callout>
          </Section>

          <Section id="faq" title="Najczęściej zadawane pytania"
                   icon={HelpCircle} chip="bg-[#FEF08A]">
            <Faq q="Co jeśli uczeń straci internet w trakcie?"
                 a="Odpowiedzi zapisują się co 1.2 s w tle. Gdy internet wróci,
                    uczeń kontynuuje od ostatniego pytania bez utraty postępu.
                    Limit czasu sesji NIE zatrzymuje się — nauczyciel widzi, że
                    uczeń jest offline." />
            <Faq q="Czy mogę edytować pytanie po jego utworzeniu?"
                 a="Tak, jeśli to Twoje pytanie. Wspólnych pytań (zatwierdzonych przez
                    administratora) nie możesz zmieniać, ale możesz utworzyć własną kopię." />
            <Faq q="Jak dopuścić kilka poprawnych odpowiedzi na pytanie otwarte?"
                 a={<>W polu <b>Poprawna odpowiedź</b> możesz podać kilka
                     akceptowalnych wariantów oddzielonych pionową kreską <code>|</code>
                     lub słowem <code>lub</code>. Przykłady:
                     <ul className="list-disc pl-6 mt-1">
                       <li><code>H2O | woda | tlenek diwodoru</code></li>
                       <li><code>Zasada wykluczania Pauliego | Zakaz Pauliego | Pauliego</code></li>
                       <li><code>metan lub CH4</code></li>
                     </ul>
                     System dopuści każdy z wariantów (z toleracją literówek i formatowania).
                     Admin ma też przycisk &bdquo;✨ Zaproponuj synonimy (AI)&rdquo; w edycji
                     pytania — Claude 4.6 sam podpowie akceptowalne warianty.</>} />
            <Faq q="Czy muszę podać prawdziwe imię przy rejestracji?"
                 a={`Nie. Pole „Nazwa wyświetlana" może zawierać pseudonim
                    (np. „Chemik42", „Pani od chemii"). Aplikacja używa tego
                    pola tylko w mailach powitalnych i podpisie w panelu.
                    Zgodnie z RODO art. 25 (privacy by design) zachęcamy
                    do pseudonimizacji.`} />
            <Faq q="Co jeśli zapomnę hasła?"
                 a={`Na ekranie logowania kliknij „Zapomniałem hasła" i wpisz adres
                    e-mail. Otrzymasz link resetujący (ważny 1 godzinę). Po zmianie
                    hasła wszystkie istniejące sesje zostaną unieważnione.`} />
            <Faq q="Czy aplikacja działa na telefonie?"
                 a="Tak — uczniowie najczęściej dołączają z telefonu, panel
                    nauczyciela działa zarówno na desktop jak i tablet/telefon
                    (responsive)." />
            <Faq q="Czy mogę usunąć swoje konto?"
                 a={`Tak — w panelu nauczyciela otwórz Ustawienia → Strefa niebezpieczna
                    → „Chcę usunąć swoje konto". Konto zostanie natychmiast wyłączone
                    (RODO art. 17), a wszystkie dane trwale skasowane po 30 dniach
                    (okres na odwołanie). Wcześniej możesz też pobrać kopię swoich
                    danych w ZIP (JSON + CSV) — RODO art. 20.`} />
            <Faq q="Ile pytań może zawierać kartkówka?"
                 a="Standardowo 10, możesz ustawić 5–30. Powyżej 30 sugerujemy
                    rozbicie na osobne sesje aby uczniowie nie tracili koncentracji." />
            <Faq q="Czy mogę puścić tę samą kartkówkę dwóm klasom?"
                 a="Tak. Każda sesja ma własny 6-znakowy kod — możesz uruchomić
                    sesję dla 1A o 9:00, a potem nową sesję z tej samej kartkówki
                    dla 1B o 11:00. Wyniki nie mieszają się." />
            <Faq q="Czy mogę zrobić kartkówkę z pytaniami z różnych klas?"
                 a='Tak — w oknie „Nowa kartkówka" przełącz się na zakładkę
                    „🔀 Cross-scope". Dla każdego tematu wybierasz osobno,
                    z jakiej klasy mają lecieć pytania (np. Mol z klasy 2 rozszerzenie
                    + Roztwory z klasy 1 podstawa). Przydatne przy powtórkach do matury.' />
            <Faq q="Jak wybrać konkretne pytania z listy, zamiast losowania?"
                 a={<>W oknie „Nowa kartkówka" wejdź w zakładkę <b>„✋ Ręczny wybór"</b>.
                    Otworzy się lista wszystkich pytań z zaznaczonego zakresu —
                    możesz filtrować po temacie i treści, a następnie ptaszkiem
                    zaznaczyć te, które mają trafić do kartkówki. Świetne
                    na wejściówki i kolokwia. <br/><br/>
                    <b>Ręczny + Grupy A/B</b>: gdy dodatkowo zaznaczysz „👥 Grupy A/B",
                    nad listą pojawią się dwa taby — <b>Grupa A</b> i <b>Grupa B</b>.
                    W każdym tabie wybierasz pytania niezależnie; pytania z drugiej grupy
                    są widoczne, ale zablokowane (chip „🔒 w innej grupie"), więc pule
                    zawsze są rozłączne.</>} />
            <Faq q="Jak zapobiec ściąganiu?"
                 a={<><b>Dwa poziomy anti-ściąganie</b> w oknie tworzenia kartkówki — możesz je
                    łączyć: <br/>
                    (1) <b>🎯 Losuj kolejność pytań per uczeń</b> — każdy uczeń dostanie
                    ten sam zestaw, ale w innej kolejności. <br/>
                    (2) <b>👥 Grupy A/B</b> — kartkówka dostanie dwa kody i dwie różne pule
                    pytań z tych samych tematów. Pół klasy wchodzi kodem A, druga połowa kodem B;
                    obie grupy wystartują i skończą się jednocześnie, a wyniki lecą do wspólnej
                    statystyki. W trybach Automatyczny/Cross-scope trzeba mieć 2× więcej pytań
                    na temat; w trybie Ręcznym wybierzesz pytania osobno dla A i B.<br/><br/>
                    <b>💡 Zaznaczenie obu opcji naraz</b> = maksymalna ochrona: A/B rozdziela
                    pule pytań, a losowa kolejność miesza sekwencję wewnątrz każdej grupy.
                    Aplikacja pokaże zielony baner potwierdzający, że kombinacja jest świadoma.</>} />
            <Faq q="Co się stanie, gdy uczeń nie zdąży kliknąć Oddaj?"
                 a='System automatycznie oddaje jego arkusz w chwili zakończenia kartkówki
                    (przez upływ czasu albo przez Twój przycisk „Zakończ"). Zapisane
                    odpowiedzi są brane pod uwagę, nieodpowiedziane pytania liczą się
                    jako błędne. Uczeń zostaje przekierowany do widoku wyników z informacją,
                    że praca została zapisana. Statystyki klasy obejmują wszystkie oddane
                    prace — bez luki „ktoś pisze".' />
            <Faq q="Czy kartkówka kończy się sama po czasie?"
                 a='Tak. Kilkanaście sekund po upływie limitu czasu system automatycznie
                    flipuje sesję na „Zakończona", auto-oddaje wszystkie nieoddane prace
                    i wysyła Ci powiadomienie na dashboardzie („⏱ Kartkówka XXX zakończyła się
                    automatycznie — oceniono N nieoddanych prac"). Możesz też zakończyć
                    wcześniej klikając „Zakończ" — działa dokładnie tak samo.' />
            <Faq q="Uczeń przypadkiem dołączył kilka razy — co robić?"
                 a='Aplikacja sama tego unika: drugie kliknięcie „Dołącz" z tego samego urządzenia
                    wraca do rozpoczętej pracy zamiast tworzyć nowy wpis. Jeśli mimo tego pojawi się
                    duplikat (np. uczeń zmienił urządzenie), na panelu sesji kliknij
                    <b> „🧹 Usuń duplikaty"</b> — zachowamy najstarszy wpis per numer w dzienniku
                    (chronione są prace już oddane).' />
            <Faq q="Jak zgłosić błąd w pytaniu z puli wspólnej?"
                 a='Otwórz zakończoną kartkówkę, przewiń do sekcji „Użyte pytania".
                    Przy każdym pytaniu ze wspólnej puli znajdziesz przycisk „Zgłoś poprawkę"
                    — otworzy się modal, w którym opiszesz problem (np. „poprawna odpowiedź
                    to B, nie A"). Administrator zobaczy oryginał + Twoją uwagę i zdecyduje
                    o poprawce.' />
            <Faq q="Czy uczniowie widzą swoje nazwiska w systemie?"
                 a='Nie. Zgodnie z zasadą minimalizacji danych (RODO), uczeń podaje tylko
                    imię i numer w dzienniku (1–40). Nawet jeśli wpisze „Anna Kowalska",
                    system zapisze tylko „Anna". Wyniki możesz jednoznacznie zidentyfikować
                    dzięki numerowi w dzienniku.' />
          </Section>

          <div className={`${BRUT} bg-[#0F172A] text-white rounded-md p-6 lg:p-8`}>
            <div className="text-[10px] tracking-[0.25em] uppercase font-black
                            text-[#FEF08A]">Nie znalazłeś odpowiedzi?</div>
            <h3 className="font-display font-black text-2xl sm:text-3xl mt-2 leading-tight">
              Napisz do nas — <span className="bg-[#FEF08A] text-[#0F172A] px-2">odpowiadamy w 24h</span>.
            </h3>
            <p className="text-[#CBD5E1] text-sm mt-3 max-w-xl">
              Sugestie, błędy, pytania o integrację z dziennikiem — wszystko trafia
              do nas na e-mail.
            </p>
            <a href="mailto:chemiainfo@kartkowka.pl"
               className={`${BRUT} ${BRUT_HOVER} inline-block bg-[#FEF08A] text-[#0F172A]
                            font-black px-5 py-2.5 rounded-md mt-5 text-sm`}>
              chemiainfo@kartkowka.pl
            </a>
          </div>
        </article>
      </div>
    </div>
  );
}

/* ---------------- Subcomponents ---------------- */

function Section({ id, title, icon: Icon, chip, children }) {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    const url = `${window.location.origin}/instrukcja#${id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Skopiowano link do sekcji");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Nie udało się skopiować — skopiuj ręcznie z paska adresu");
    }
  };

  return (
    <section id={id} className="scroll-mt-24" data-testid={`section-${id}`}>
      <div className="flex items-center gap-3 mb-6 group">
        <div className={`${BRUT} ${chip} w-12 h-12 flex items-center justify-center rounded-md`}>
          <Icon className="w-6 h-6" strokeWidth={2.5} />
        </div>
        <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl
                       tracking-tight leading-tight flex-1 min-w-0">{title}</h2>
        <button
          onClick={copyLink}
          aria-label={`Skopiuj link do sekcji ${title}`}
          title="Kopiuj link do tej sekcji"
          data-testid={`copy-section-link-${id}`}
          className={`${BRUT} ${BRUT_HOVER} bg-white rounded-md w-9 h-9 flex items-center
                     justify-center shrink-0 lg:opacity-60 lg:group-hover:opacity-100
                     transition-opacity`}
        >
          {copied
            ? <Check className="w-4 h-4 text-[#059669]" strokeWidth={3} />
            : <Link2 className="w-4 h-4" strokeWidth={2.5} />}
        </button>
      </div>
      <div className="space-y-5 text-[15px] text-[#334155]">{children}</div>
    </section>
  );
}

function StepRow({ n, title, body, icon: Icon }) {
  return (
    <div className={`${BRUT} bg-white rounded-md p-5 flex gap-4 items-start`}>
      <div className={`${BRUT} bg-[#F8FAFC] w-12 h-12 flex items-center
                        justify-center rounded-md shrink-0`}>
        {Icon
          ? <Icon className="w-5 h-5" strokeWidth={2.5} />
          : <span className="font-display font-black text-xl">{n}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-black text-lg text-[#0F172A]">{title}</div>
        <div className="text-[#334155] mt-1 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

function Callout({ color, children }) {
  return (
    <div className={`${BRUT} ${color} rounded-md p-4 mt-4 text-sm leading-relaxed
                      text-[#0F172A]`}>
      {children}
    </div>
  );
}

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`${BRUT} bg-white rounded-md overflow-hidden`}>
      <button onClick={() => setOpen((o) => !o)}
              data-testid={`faq-toggle-${q.slice(0, 20).replace(/\s+/g, "-")}`}
              className="w-full text-left flex items-center gap-3 p-4
                         hover:bg-[#F8FAFC] transition-colors">
        <span className="font-bold flex-1 text-[#0F172A]">{q}</span>
        <ChevronDown className={`w-5 h-5 shrink-0 transition-transform
                                  ${open ? "rotate-180" : ""}`}
                     strokeWidth={2.5} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 text-sm text-[#334155] border-t-2 border-[#0F172A]
                         leading-relaxed bg-[#F8FAFC]">
          {a}
        </div>
      )}
    </div>
  );
}
